import { viewerApi } from "@shared/apis"
import { PaymentInformation, Role, Viewer } from "@shared/contexts"
import {
  db,
  fbConnect,
  fbDisConnect,
  getIsAccessToken,
  getRef,
  getTimestamp,
  kick,
  setTimestamp,
} from "@shared/modules"
import { Device } from "@shared/types"

import { Chance } from "chance"
import dayjs from "dayjs"

function toSnakeCase(
  viewer: Viewer,
  timestamp: number,
  device: Device,
  serverTimestamp: Object,
): FirebaseViewer {
  return {
    user_id: viewer.userId,
    username: viewer.username,
    nickname: viewer.nickname,
    role: viewer.role,
    timestamp,
    device,
    server_timestamp: serverTimestamp,
    is_blocked_chat: viewer.isBlockedChat,
    uuid: viewer.uuid,
  } as FirebaseViewer
}

export default {
  toSnakeCase,
  toCamelCase: (firebaseViewer: FirebaseViewer) => {
    return {
      userId: firebaseViewer.user_id,
      nickname: firebaseViewer.nickname,
      username: firebaseViewer.username,
      role: firebaseViewer.role,
      timestamp: firebaseViewer.timestamp,
      isKicked: firebaseViewer.is_kicked,
      isBlockedChat: firebaseViewer.is_blocked_chat,
      device: firebaseViewer.device,
      uuid: firebaseViewer.uuid,
    } as Viewer
  },
  // forceAnonymous: 토큰 유무와 관계없이 익명 시청자로 고정한다.
  // 숏폼 비회원은 재생 페이지에서 토큰을 늦게 받으므로, 이전 진입에서 남은 토큰으로
  // viewers/status를 호출해 회원으로 판정되는 일이 없도록 한다.
  get: async (forceAnonymous = false): Promise<Viewer | undefined> => {
    if (!forceAnonymous && getIsAccessToken()) {
      try {
        const { data: viewer } = await viewerApi.status()

        return viewer
      } catch ({ response }) {
        kick(response?.data?.message)
        return
      }
    } else {
      const randomName = Chance().name()

      return {
        username: randomName,
        nickname: randomName,
        role: "ANONYMOUS",
        isKicked: false,
        isBlockedChat: false,
      } as Viewer
    }
  },
  detectConnection: (
    scheduleKey: string,
    paymentInfo: PaymentInformation,
    fbUniqueId: string,
    viewer: Viewer,
    setWatchingViewerRef: Function,
    isDisConnectedRef: any,
  ): firebase.database.Reference => {
    fbDisConnect()

    const connectedRef = fbConnect()
    const dailyUserRole: DailyUserRole =
      viewer.role === "ANONYMOUS" ? "anonymous" : "member"
    let disconnectedAt: number | null = null

    connectedRef.on(
      "value",
      async (snapshot: firebase.database.DataSnapshot) => {
        if (snapshot.val() === true) {
          if (isDisConnectedRef.current === true) {
            const reconnectedAt = Date.now()
            const duration = disconnectedAt ? reconnectedAt - disconnectedAt : null
            console.log("[Firebase] 재연결 성공", {
              disconnectedAt: disconnectedAt ? new Date(disconnectedAt).toISOString() : null,
              reconnectedAt: new Date(reconnectedAt).toISOString(),
              durationMs: duration,
            })
            disconnectedAt = null
            await new Promise((resolve) => setTimeout(resolve, Math.random() * 3000))
          } else if (isDisConnectedRef.current === undefined) {
            console.log("[Firebase] 최초 연결 성공")
          }

          const viewerRef = getRef("viewers")
            .child(scheduleKey)
            .child(fbUniqueId)

          viewerRef.onDisconnect().remove()

          await getRef("viewer_devices")
            .child(scheduleKey)
            .child(fbUniqueId)
            .set(window.navigator.userAgent)

          const timestamp = Number(getTimestamp() || setTimestamp())

          await viewerRef
            .set(
              toSnakeCase(
                viewer,
                timestamp,
                "PC_WEB",
                db.ServerValue.TIMESTAMP,
              ),
            )
            .then(() => {
              if (isDisConnectedRef.current !== undefined) return
              isDisConnectedRef.current = false
            })

          const currentTime =
            (await (
              await viewerRef.child("server_timestamp").once("value")
            ).val()) || timestamp

          const dailyUsersRef = getRef("daily_users")
            .child(dayjs(currentTime).format("YYYY-MM-DD"))
            .child(paymentInfo.toLowerCase())
            .child("pc_web")
            .child(dailyUserRole)
            .child(`${scheduleKey}@${fbUniqueId}`)

          dailyUsersRef.onDisconnect().remove()
          dailyUsersRef.set(true)

          setWatchingViewerRef(viewerRef)
        } else {
          if (isDisConnectedRef.current === undefined) return

          disconnectedAt = Date.now()
          console.log("[Firebase] 연결 끊김 감지", {
            disconnectedAt: new Date(disconnectedAt).toISOString(),
          })

          isDisConnectedRef.current = true
        }
      },
    )

    return connectedRef
  },
}

type DailyUserRole = "member" | "anonymous"

interface FirebaseViewer {
  user_id: number
  nickname: string
  username: string
  role: Role
  timestamp: number
  server_timestamp?: Object
  is_kicked?: boolean
  is_blocked_chat: boolean
  device?: string
  uuid: string | undefined
}
